import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Bot, Plus, Send } from 'lucide-react';
import { MarkdownContent } from '@/financeBotV2/src/components/ui/markdown-content';
import { useQueryStream } from '@/financeBotV2/src/hooks/useQueryStream';
import { getSmartMorningStatusStage } from './smartMorningChatStatus';

type Message = {
  id: number;
  role: 'assistant' | 'user';
  text: string;
  smartInsightsQuestion?: string;
};

const starterPrompts = [
  'Show the top 3 customers by sales',
  'Show the bottom 5 customers by product contribution',
  'Which channels have contribution above 70%?',
];

const arStarterPrompts = [
 'Which 5 customers have the largest past-due AR balances?',
 'How much open AR is more than 90 days past due, by company?',
 'Which customers are paying the slowest this year?',
];

type SmartMorningChatInterfaceProps = {
 parameters: Record<string, unknown>;
 cacheReady?: boolean;
 templateId?: string | null;
};

export default function SmartMorningChatInterface({ parameters, cacheReady = true, templateId }: SmartMorningChatInterfaceProps) {
  const prompts = templateId === 'smart_morning_ar' ? arStarterPrompts : starterPrompts;
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const {
    progress,
    response,
    isLoading: sending,
    error,
    conversationId,
    currentQuery,
    sendQuery,
    reset,
  } = useQueryStream();

  const statusStageRef = useRef('Understanding your question');
  const statusStage = sending
    ? getSmartMorningStatusStage(progress, statusStageRef.current)
    : 'Understanding your question';
  if (sending) statusStageRef.current = statusStage;
  else statusStageRef.current = 'Understanding your question';
  const handledResponseQuery = useRef<string | null>(null);
  const handledErrorQuery = useRef<string | null>(null);

  useEffect(() => {
    if (conversationId) setThreadId(conversationId);
  }, [conversationId]);

  useEffect(() => {
    if (response && !sending && currentQuery && handledResponseQuery.current !== currentQuery) {
      const responseData = response as {
        normal_text?: string;
        summary?: string;
        analysis_summary?: { summary_text?: string };
      };
      const answer = responseData.normal_text
        || responseData.summary
        || responseData.analysis_summary?.summary_text
        || '';
      if (!answer.trim()) return;
      setMessages((current) => [...current, {
        id: Date.now(),
        role: 'assistant',
        text: answer,
      }]);
      handledResponseQuery.current = currentQuery;
    }
  }, [currentQuery, response, sending]);

  useEffect(() => {
    if (error && !sending && currentQuery && handledErrorQuery.current !== currentQuery) {
      setMessages((current) => [...current, {
        id: Date.now(),
        role: 'assistant',
        text: error,
      }]);
      handledErrorQuery.current = currentQuery;
    }
  }, [currentQuery, error, sending]);

  const submitMessage = async (value = draft) => {
    const text = value.trim();
    if (!text || sending) return;

    const userMessageId = Date.now();
    setMessages((current) => [...current, { id: userMessageId, role: 'user', text }]);
    setDraft('');
    sendQuery(text, undefined, 'simple', threadId, undefined, parameters);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitMessage();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };

  const startNewChat = () => {
    if (sending) return;
    setMessages([]);
    setThreadId(null);
    reset();
    setDraft('');
  };

  return (
    <section className="overflow-hidden rounded-xl border border-[#E4D9D3] bg-[#FCFAF8] shadow-[0_16px_40px_rgba(79,32,54,0.08)]" aria-labelledby="smartmorning-chat-title">
        <div className="flex min-h-[480px] flex-col">
          <div className="flex items-center justify-between border-b border-[#EDE6E1] px-5 py-4 sm:px-7">
            <div>
              <h2 id="smartmorning-chat-title" className="font-display text-sm font-semibold text-[#1F1A20]">Ask about this report</h2>
            </div>
            <button
              type="button"
              onClick={startNewChat}
              disabled={sending}
              title="New Chat"
              aria-label="New Chat"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#6E3450] text-white transition-colors hover:bg-[#5A2A41] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F47B20] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-auto px-5 py-6 sm:px-7">
            {messages.map((message) => (
              <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}>
                {message.role === 'assistant' && (
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4E8EE] text-[#6E3450]">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div className={`w-fit rounded-2xl px-4 py-3 text-sm leading-6 ${message.role === 'user' ? 'max-w-[34rem] rounded-br-sm bg-[#6E3450] text-white' : 'max-w-full rounded-tl-sm border border-[#E8DED9] bg-white text-[#45373E] sm:max-w-[60%]'}`}>
                  {message.role === 'assistant' ? (
                    <>
                      <MarkdownContent content={message.text} className="[&_*]:text-[#45373E] [&_p]:my-1.5 [&_p]:leading-6 [&_strong]:font-semibold [&_strong]:text-[#45373E]" />
                      {message.smartInsightsQuestion && (
                        <button
                          type="button"
                          onClick={() => navigate(`/smart-insights?question=${encodeURIComponent(message.smartInsightsQuestion ?? '')}`)}
                          className="mt-3 rounded-lg bg-[#6E3450] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[#5A2A41] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F47B20]"
                        >
                          Open in Smart Insights
                        </button>
                      )}
                    </>
                  ) : message.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3" aria-live="polite" aria-label={`SmartMorning: ${statusStage}`}>
                <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4E8EE] text-[#6E3450]">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-[#E8DED9] bg-white px-4 py-3 text-sm text-[#45373E]">
                  <span>{statusStage}</span>
                  {[0, 1, 2].map((index) => (
                    <span key={index} className="h-2 w-2 animate-pulse rounded-full bg-[#6E3450]" style={{ animationDelay: `${index * 180}ms` }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#EDE6E1] px-5 py-4 sm:px-7">
            <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
              {prompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => submitMessage(prompt)}
                  className="group inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#E2D3DB] bg-white px-3 py-2 text-xs font-medium text-[#6E3450] transition-colors hover:border-[#6E3450] hover:bg-[#F8EFF3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F47B20]"
                >
                  {prompt}
                  <ArrowUpRight className="h-3 w-3 opacity-60 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="flex items-end gap-3 rounded-xl border border-[#DCCED4] bg-white p-2 shadow-sm focus-within:border-[#6E3450] focus-within:ring-2 focus-within:ring-[#F4E8EE]">
              <label htmlFor="smartmorning-chat-draft" className="sr-only">Ask SmartMorning a question</label>
              <textarea
                id="smartmorning-chat-draft"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                placeholder="e.g. What is driving the decline in product contribution?"
                className="min-h-[48px] flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm text-[#1F1A20] outline-none placeholder:text-[#A99CA2]"
              />
              <button
                type="submit"
                aria-label="Send question"
                disabled={!draft.trim() || sending}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#6E3450] text-white transition-colors hover:bg-[#5A2A41] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F47B20] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            <p className="mt-2 text-[11px] text-[#A09298]">Enter to send · Shift + Enter for a new line</p>
          </div>
        </div>
    </section>
  );
}
